import { shadowVoice, type VoiceSpace } from '../../content/voices/voice-spaces.js';

/**
 * The battle mix: how loud each kind of sound sits, how many of a kind may ring at once, and which wins when there is no room.
 * Levels are in dB against a loudness-matched recording (the engine evens every matched file out to the same loudness as it
 * decodes it), so a new recording dropped into a folder lands at its kind's level without hand trimming.
 */
export type SoundKind = 'hit' | 'crit' | 'heavy-hit' | 'swing' | 'miss' | 'block' | 'bark' | 'cantrip-release' | 'cantrip-hit' | 'breath'
  | 'spark-step' | 'initiative' | 'arc' | 'potion' | 'critter-call' | 'critter-wings' | 'critter-munch' | 'ping' | 'condition' | 'command'
  | 'rage-burst' | 'heartbeat' | 'spirit' | 'shadow' | 'shadow-whisper';

interface KindMix { /** dB against a matched recording. */ level: number; /** How many of this kind ring at once. */ cap: number; /** Higher wins a crowded moment. */ priority: number; /** A room the kind rings in. */ space?: VoiceSpace }

export const MIX: Record<SoundKind, KindMix> = {
  /** A blade, bite or arrow landing. */
  'hit': { level: 2, cap: 6, priority: 3 },
  /** A critical hit: the hit recording, louder and deeper (`combat-sounds.ts`). */
  'crit': { level: 6, cap: 3, priority: 4 },
  /** Sneak Attack: its own recording, the strongest blow. */
  'heavy-hit': { level: 9, cap: 3, priority: 4 },
  /** An arrow loosed. */
  'swing': { level: -3, cap: 3, priority: 1 },
  /** A whiff or an arrow landing wide: it answers a swing, so it must be heard as clearly. */
  'miss': { level: 1, cap: 4, priority: 2 },
  /** A block or an ally stepping in. */
  'block': { level: 2, cap: 2, priority: 3 },
  /** Monster shouts, one per faction at a time (`combat-voice-channel.ts`); each event's own accent (0.75 to 0.9) comes on top. */
  'bark': { level: 0, cap: 3, priority: 3 },
  'cantrip-release': { level: -4, cap: 3, priority: 2 },
  'cantrip-hit': { level: 1, cap: 3, priority: 3 },
  /** Breath of Life's inhale and the healing breath. */
  'breath': { level: -3, cap: 2, priority: 2 },
  /** A monster blinking away with Spark Step. */
  'spark-step': { level: 0, cap: 2, priority: 3 },
  /** Arc Lightning's discharge, and its jump to a second target at an accent below it. */
  'arc': { level: 0, cap: 3, priority: 3 },
  /** A healing potion drunk. */
  'potion': { level: -2, cap: 2, priority: 2 },
  /** The drumbeat as a fight begins: one for the whole party. */
  'initiative': { level: -2, cap: 1, priority: 3 },
  /** Wildlife in the background of a fight or a walk: a crow's caw, a gull's cry, a bleat. Last to ring, first to give way. */
  'critter-call': { level: -8, cap: 3, priority: 1 },
  'critter-wings': { level: -11, cap: 3, priority: 1 },
  'critter-munch': { level: -13, cap: 2, priority: 1 },
  /** The whistle that orders a Coordinated Strike. */
  'command': { level: -4, cap: 1, priority: 2 },
  /** Dazed, Frightened or Prone landing on a unit. */
  'condition': { level: -3, cap: 3, priority: 2 },
  /** A ping dropped on the map, by any player. */
  'ping': { level: -4, cap: 2, priority: 2 },
  /** God's Strength: the fire bursting up as a Berserker Rages. */
  'rage-burst': { level: 3, cap: 2, priority: 3 },
  /** A Raging Berserker's pulse, one double thump a beat, kept low under the fight. */
  'heartbeat': { level: -9, cap: 2, priority: 1 },
  /** The Lifebinding Spirit arriving and leaving: a backdrop to the fight, well under the blows. */
  'spirit': { level: -7, cap: 2, priority: 2 },
  /** A Shadow rising beside the Shadowmancer or dissolving. */
  'shadow': { level: -4, cap: 3, priority: 2 },
  /** The whisper under Summon and Command Shadows, low and in the same dark hall as the Shadowmancer's voice. */
  'shadow-whisper': { level: -8, cap: 1, priority: 2, space: shadowVoice.space },
};
/** One-shot effects ringing at once across every kind; past this a new sound replaces the weakest or is dropped. */
export const MAX_EFFECTS = 16;
/** A hero's spoken line, against a matched recording. */
export const HERO_VOICE_DB = 0;
/** A cast's channel bed under the incantation and the orb. */
export const CAST_BED_DB = -10;
/** Combat music sits above exploration music so it is not buried by the fight. */
export const COMBAT_MUSIC_DB = 4;

export const decibels = (db: number) => 10 ** (db / 20);
export const levelOf = (kind: SoundKind) => decibels(MIX[kind].level);

/** Seconds a replaced sound takes to fade out. */
const STEAL_FADE = .06;
interface Ringing { kind: SoundKind; priority: number; started: number; ends: number; stop: (fade?: number) => void }

/**
 * Keeps a crowded fight readable: at most `cap` sounds of a kind and `MAX_EFFECTS` in all ring at once. When there is no room, the
 * weakest ringing sound (lowest priority, then the oldest, whose attack has already been heard) fades out for the new one; if
 * everything ringing outranks the newcomer, the newcomer is dropped.
 */
export class EffectVoices {
  private ringing: Ringing[] = [];

  /** Whether a sound of `kind` may start at `now` (seconds); makes room for it first when it outranks what it replaces. */
  admit(kind: SoundKind, now: number): boolean {
    this.ringing = this.ringing.filter(sound => sound.ends > now);
    const same = this.ringing.filter(sound => sound.kind === kind);
    const crowd = same.length >= MIX[kind].cap ? same : this.ringing.length >= MAX_EFFECTS ? this.ringing : [];
    if (!crowd.length) return true;
    const weakest = crowd.reduce((a, b) => b.priority < a.priority || b.priority === a.priority && b.started < a.started ? b : a);
    if (weakest.priority > MIX[kind].priority) return false;
    weakest.stop(STEAL_FADE);
    this.ringing.splice(this.ringing.indexOf(weakest), 1);
    return true;
  }

  /** Records a sound that has started and rings for `seconds`. */
  add(kind: SoundKind, now: number, seconds: number, stop: (fade?: number) => void) {
    this.ringing.push({ kind, priority: MIX[kind].priority, started: now, ends: now + seconds, stop });
  }

  get count() { return this.ringing.length; }
}
